import { Editor, Element, Path, Range, Transforms } from 'slate';

import * as serializer from 'basic-editor-value-serializer';

import { insertAfterBlock, isInRow } from './wrap';
import { insertKeepingRowItem } from './keep-in-item';
import { deleteAcrossItems, isCrossingSelection } from './cross-delete';
import { findNearestLayout } from './boundary';

const types = serializer.types;
const layoutUtilities = serializer.utilities.node.layout;

const COLLECTION_ITEMS = [types.GALLERY_ITEM, types.CAROUSEL_ITEM];

function isBlockElement(editor, node) {
  return Element.isElement(node) === true && Editor.isBlock(editor, node) === true;
}

// The block Enter adds after: the lowest block at the caret, or the gallery holding the item.
function findCaretBlock(editor) {

  const entry = Editor.above(editor, { match: isBlockElement.bind(null, editor), mode: 'lowest' });

  if (typeof entry === 'undefined') {
    return null;
  }

  if (COLLECTION_ITEMS.indexOf(entry[0].type) !== -1) {
    return Editor.node(editor, Path.parent(entry[1]));
  }

  return entry;

}

function findExitTarget(editor, layoutPath) {

  let target = layoutPath;

  while (isInRow(editor, target) === true) {
    target = Path.parent(target);
  }

  return target;

}

function isLastOfSeveral(layout, layoutPath, blockPath) {

  if (layout.children.length < 2 || Path.isChild(blockPath, layoutPath) === false) {
    return false;
  }

  return blockPath[blockPath.length - 1] === layout.children.length - 1;

}

// Rule 2: Enter in an empty last paragraph of a stack leaves the stack, and any rows it sits in.
function tryLeaveStack(editor, block, blockPath, layout, layoutPath) {

  if (block.type !== types.DEFAULT || Editor.isEmpty(editor, block) === false) {
    return false;
  }

  if (layoutUtilities.isRowLayout(layout) === true || isLastOfSeveral(layout, layoutPath, blockPath) === false) {
    return false;
  }

  const paragraphPath = Path.next(findExitTarget(editor, layoutPath));

  Editor.withoutNormalizing(editor, function leaveStack() {
    Transforms.removeNodes(editor, { at: blockPath });
    Transforms.insertNodes(editor, serializer.utilities.node.paragraph.buildNode(), { at: paragraphPath });
  });
  Transforms.select(editor, Editor.start(editor, paragraphPath));
  return true;

}

// Rule 1 for an item of a row: the break runs as today, then anything it added to the row is
// stacked with the item. At the depth limit the break becomes a soft line break.
function breakInRowItem(editor, block, blockPath, layoutPath) {

  const itemPath = blockPath.slice(0, layoutPath.length + 1);

  if (Editor.isVoid(editor, block) === true || block.type === types.GALLERY || block.type === types.CAROUSEL) {
    if (Path.equals(itemPath, blockPath) === false) {
      return false;
    }
    const insertedPath = insertAfterBlock(editor, blockPath, [serializer.utilities.node.paragraph.buildNode()]);
    if (insertedPath !== null) {
      Transforms.select(editor, Editor.start(editor, insertedPath));
    }
    return true;
  }

  if (insertKeepingRowItem(editor, true, editor.insertBreak.bind(editor)) === false) {
    Editor.insertText(editor, '\n');
  }

  return true;

}

// Enter inside a layout (spec 3.5). Returns true when it handled the key.
export function handleEnterKey(editor) {

  if (isCrossingSelection(editor) === true) {
    deleteAcrossItems(editor);
  }

  if (editor.selection === null || Range.isCollapsed(editor.selection) === false) {
    return false;
  }

  const blockEntry = findCaretBlock(editor);

  if (blockEntry === null) {
    return false;
  }

  const layoutEntry = findNearestLayout(editor, blockEntry[1]);

  if (layoutEntry === null) {
    return false;
  }

  if (layoutUtilities.isRowLayout(layoutEntry[0]) === true) {
    return breakInRowItem(editor, blockEntry[0], blockEntry[1], layoutEntry[1]);
  }

  return tryLeaveStack(editor, blockEntry[0], blockEntry[1], layoutEntry[0], layoutEntry[1]);

}
